import { AlertConfig } from '../contacts/user.service';

/**
 * Platform caps the web app applies before Save and before ALERT stamp.
 * Do not put these in `environment.ts` — they are product defaults, not deploy URLs.
 * The Alert Engine uses whatever snapshot is sent; it does not invent these.
 */
export const PLATFORM_ALERT_LIMITS = {
  maxRetryAttempts: 4,
  minRetryIntervalMinutes: 15,
} as const;

/** Platform notify window — on `alertConfig`, not an org form field. Engine does not invent this. */
export const DEFAULT_NOTIFY_WINDOW = {
  start: '07:00',
  end: '19:00',
  timeZone: 'America/New_York',
} as const;

export const DEFAULT_TARGET_TIME = '09:00';

/** Ghost-text, Use default SMS, and missing-org fallback at stamp time. */
export const DEFAULT_ALERT_CONFIG: AlertConfig = {
  subjectLabel: 'Patient',
  subjectLabelPlural: 'Patients',
  retryAttempts: PLATFORM_ALERT_LIMITS.maxRetryAttempts,
  retryIntervalMinutes: 20,
  smsBody:
    'ALERT: Please call {org} at {phone} regarding {subjectName}. This is an automated alert — we will text and call you every {interval}, up to {retries} times, until someone responds. Msg&Data rates may apply.',
  pressZeroToConnect: true,
  urlTitle: '',
  urlHyperlink: '',
  notifyWindow: { ...DEFAULT_NOTIFY_WINDOW },
  targetTime: DEFAULT_TARGET_TIME,
  noText: false,
  noCall: false,
};

export const SUBJECT_LABEL_MAX = 20;
export const SMS_BODY_MAX = 1600;

/** Org `alertConfig` over defaults. Clamp retries to the platform max and interval to the floor. */
export function resolveAlertConfig(saved?: Partial<AlertConfig> | null): AlertConfig {
  const retryAttempts = saved?.retryAttempts ?? DEFAULT_ALERT_CONFIG.retryAttempts;
  const retryIntervalMinutes =
    saved?.retryIntervalMinutes ?? DEFAULT_ALERT_CONFIG.retryIntervalMinutes;
  return {
    subjectLabel: saved?.subjectLabel?.trim() || DEFAULT_ALERT_CONFIG.subjectLabel,
    subjectLabelPlural: saved?.subjectLabelPlural?.trim() || DEFAULT_ALERT_CONFIG.subjectLabelPlural,
    retryAttempts: Math.min(Math.max(1, retryAttempts), PLATFORM_ALERT_LIMITS.maxRetryAttempts),
    retryIntervalMinutes: Math.max(
      PLATFORM_ALERT_LIMITS.minRetryIntervalMinutes,
      retryIntervalMinutes,
    ),
    smsBody: saved?.smsBody?.trim() || DEFAULT_ALERT_CONFIG.smsBody,
    pressZeroToConnect: saved?.pressZeroToConnect ?? DEFAULT_ALERT_CONFIG.pressZeroToConnect,
    urlTitle: saved?.urlTitle?.trim() ?? DEFAULT_ALERT_CONFIG.urlTitle,
    urlHyperlink: saved?.urlHyperlink?.trim() ?? DEFAULT_ALERT_CONFIG.urlHyperlink,
    notifyWindow: saved?.notifyWindow ?? DEFAULT_ALERT_CONFIG.notifyWindow,
    targetTime: saved?.targetTime?.trim() || DEFAULT_ALERT_CONFIG.targetTime,
    noText: saved?.noText ?? DEFAULT_ALERT_CONFIG.noText,
    noCall: saved?.noCall ?? DEFAULT_ALERT_CONFIG.noCall,
  };
}
